/**
 * RepeaterNode - Creates multiple instances from a connected source.
 *
 * Two modes, auto-detected from the source connection:
 * - **Definition mode**: source is a definition instance (GraphInstance with definitionId)
 *   → template mode with mirror endpoints, per-instance/uniform classification.
 * - **Direct mode**: source is a bare node (e.g. TurtleNode in root)
 *   → use source output as-is, apply transforms, produce N copies.
 *
 * Connect any node's output to the `source` internal input to drive the repeater.
 */

import { BaseNode } from '@nodes/BaseNode';
import { params, internals } from '@nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { Dependencies, RepeaterApi, RepeaterDomainAdapter } from '@nodes/Dependencies';
import type { Payload } from '@graph/Payload';
import type { IEndpoint } from '@runtime/IEndpoint';
import { pickCyclic, getCyclic } from '@/nodes/arrayUtils';
import type { Interaction2D } from '@/nodes/Interaction2D';
import { logger } from '@/logging';

// ─────────────────────────────────────────────────────────────
// Endpoints
// ─────────────────────────────────────────────────────────────

const repeaterEndpoints = {
  ...params({
    count: { order:0,value: 1, min: 1, max: 64, step: 1, label: 'Count' },
  }),
  ...internals({
    source: { order:1,dataType: 'any', isInput: true, accepts: ['polylines', 'three:group', 'any'], label: 'Source' },
  }),
};

// ─────────────────────────────────────────────────────────────
// Node
// ─────────────────────────────────────────────────────────────

class RepeaterNode extends BaseNode<typeof repeaterEndpoints> {
  private repeaterApi: RepeaterApi;
  private childIds: string[] = [];

  /** The source endpoint that drives this repeater */
  private sourceEndpoint: IEndpoint | null = null;

  /** Definition ID discovered from source (null = direct mode) */
  private sourceDefId: string | null = null;

  /** Node ID of the source node (for getSiblingValue in definition mode) */
  private sourceNodeId: string | null = null;

  /** Active domain adapter (null = no adapter available) */
  private adapter: RepeaterDomainAdapter | null = null;

  /** Per-instance endpoint IDs: have options, create template groups */
  private perInstanceEndpointIds: string[] = [];

  /** Uniform endpoint IDs: no options, broadcast same value to all */
  private uniformEndpointIds: string[] = [];

  /** Transform endpoint IDs injected by the adapter (per-instance arrays) */
  private transformEndpointIds: string[] = [];

  /** Option endpoint IDs injected by the adapter (scalar settings) */
  private optionEndpointIds: string[] = [];

  /** Mirror output endpoint IDs (from definition API) */
  private outputEndpointIds: string[] = [];

  /** Guard against re-entrant rebuild (trigger cascade from pushDefinition) */
  private rebuilding = false;

  /** Prebuilt objects from template/direct mode (e.g. InstancedMesh) for scene builder */
  private prebuiltObjects: any[] = [];

  /** Cached template outputs for transform-only updates (avoids re-creating templates) */
  private cachedTemplateOutputs: { output: any; slotIndices: number[] }[] = [];

  /** Cached config key to detect when config actually changed */
  private cachedConfigKey = '';

  /** Direct source output (bare node, no definition) */
  private directSourceOutput: any = null;

  /** Dynamic output endpoint created in direct mode (cleaned up on disconnect) */
  private directOutputId: string | null = null;

  constructor(id: string, deps: Dependencies) {
    super(id, deps);
    if (!deps.repeaterApi) {
      throw new Error('RepeaterNode requires repeaterApi in dependencies');
    }
    this.repeaterApi = deps.repeaterApi;
  }

  initialize(): void {
    super.initialize();
    logger.trace(`[RepeaterNode] initialize ${this.id}`);
  }

  onInternalChanged(payload: Payload, endpointId: string, source: IEndpoint): void {
    if (endpointId !== 'source') return;

    // Detect source on first connection (or source change)
    if (!this.sourceEndpoint || source !== this.sourceEndpoint) {
      this.sourceEndpoint = source;
      this.sourceNodeId = source.node.id;

      // Check if source is a definition instance (GraphInstance has definitionId)
      const defId = (source.node as any).definitionId;
      this.sourceDefId = defId && defId !== 'root' ? defId : null;

      this.rebuild();
    } else {
      // Source value changed — update accordingly
      if (this.sourceDefId) {
        // Definition mode: source output changed, rebuild templates
        this.rebuild();
      } else {
        // Direct mode: source output changed, re-transform
        this.directSourceOutput = payload.value;
        this.rebuildDirect();
      }
    }
  }

  onDisconnected(endpointId: string, _source: IEndpoint): void {
    if (endpointId === 'source') {
      this.sourceEndpoint = null;
      this.sourceDefId = null;
      this.sourceNodeId = null;
      this.directSourceOutput = null;
      this.disposeChildren();
      this.prebuiltObjects = [];
      this.cachedTemplateOutputs = [];
      this.setOutput([]);

      // Remove dynamic output endpoint from direct mode
      if (this.directOutputId) {
        this.deps.endpointApi.removeCustomParam(this.directOutputId);
        this.directOutputId = null;
        this.outputEndpointIds = [];
      }
    }
  }

  onParamChanged(_payload: Payload, endpointId: string): void {
    if (endpointId === 'count') {
      if (this.sourceDefId) {
        this.rebuildTemplated();
      } else if (this.directSourceOutput) {
        this.rebuildDirect();
      }
    } else {
      if (this.adapter) {
        // Template/direct mode: route to appropriate fast path
        const isTransform = this.transformEndpointIds.includes(endpointId);
        const isOption = this.optionEndpointIds.includes(endpointId);
        if ((isTransform || isOption) && this.cachedTemplateOutputs.length > 0) {
          this.updateTransformsOnly();
        } else if (this.uniformEndpointIds.includes(endpointId) && this.cachedTemplateOutputs.length > 0) {
          // If the value is a non-empty array, it changes the grouping — full rebuild
          const value = this.deps.endpointApi.getValue(endpointId);
          if (Array.isArray(value) && value.length > 0) {
            this.rebuildTemplated();
          } else {
            this.updateUniformParam(endpointId);
          }
        } else if (this.sourceDefId) {
          this.rebuildTemplated();
        }
      }
    }
  }

  /** Duck-typed method for scene builder to collect prebuilt objects */
  getPrebuiltObjects(): any[] {
    return this.prebuiltObjects;
  }

  /** Duck-typed method for 2D canvas pointer interaction */
  getInteraction2D(): Interaction2D {
    let capturedIndex = -1;
    let startDataX = 0;
    let startDataY = 0;
    let origPosX: number[] = [];
    let origPosY: number[] = [];

    return {
      onPointerDown: (dataX: number, dataY: number, radius: number): boolean => {
        // Delegate to adapter for domain-specific hit testing (e.g. polyline segments),
        // fall back to generic center-based testing (posX/posY transform offsets)
        let bestIndex = -1;

        if (this.adapter?.hitTest) {
          bestIndex = this.adapter.hitTest(this.prebuiltObjects, dataX, dataY, radius);
        } else {
          const posX: number[] = this.deps.endpointApi.getValue('posX') ?? [];
          const posY: number[] = this.deps.endpointApi.getValue('posY') ?? [];
          const count = this.params.count.value;
          const r2 = radius * radius;

          for (let i = count - 1; i >= 0; i--) {
            const px = getCyclic(posX, i, 0);
            const py = getCyclic(posY, i, 0);
            const dx = dataX - px;
            const dy = dataY - py;
            if (dx * dx + dy * dy <= r2) {
              bestIndex = i;
              break;
            }
          }
        }

        if (bestIndex < 0) return false;

        const posX: number[] = this.deps.endpointApi.getValue('posX') ?? [];
        const posY: number[] = this.deps.endpointApi.getValue('posY') ?? [];

        capturedIndex = bestIndex;
        startDataX = dataX;
        startDataY = dataY;
        origPosX = [...posX];
        origPosY = [...posY];
        return true;
      },

      onPointerMove: (dataX: number, dataY: number): void => {
        if (capturedIndex < 0) return;

        const dx = dataX - startDataX;
        const dy = dataY - startDataY;
        const newPosX = [...origPosX];
        const newPosY = [...origPosY];
        while (newPosX.length <= capturedIndex) newPosX.push(0);
        while (newPosY.length <= capturedIndex) newPosY.push(0);

        newPosX[capturedIndex] = (origPosX[capturedIndex] ?? 0) + dx;
        newPosY[capturedIndex] = (origPosY[capturedIndex] ?? 0) + dy;

        this.deps.endpointApi.setValue('posX', newPosX);
        this.deps.endpointApi.setValue('posY', newPosY);
      },

      onPointerUp: (): void => {
        capturedIndex = -1;
      },
    };
  }

  // ─── Rebuild (mode selection) ────────────────────────────────

  private rebuild(): void {
    if (this.rebuilding) return;
    this.rebuilding = true;
    try {
      const t0 = performance.now();
      this.disposeChildren();
      this.prebuiltObjects = [];
      this.cachedTemplateOutputs = [];
      this.cachedConfigKey = '';
      // Nullify adapter before sync — removeCustomParam triggers onParamChanged,
      // and we must not run template-mode paths with stale cached data.
      this.adapter = null;

      if (!this.sourceDefId) {
        // Direct mode: adapter from source endpoint's dataType
        const sourceType = (this.sourceEndpoint as any)?.dataType as string | undefined;
        this.adapter = sourceType ? this.repeaterApi.getAdapter([sourceType]) ?? null : null;
        if (this.adapter) {
          this.injectTransformEndpoints();
          this.classifyEndpoints();

          // Create a typed output endpoint so the repeated output is visible/connectable
          if (sourceType && !this.directOutputId) {
            this.directOutputId = sourceType;
            const existing = new Set(this.deps.endpointApi.getEndpointIds());
            if (!existing.has(sourceType)) {
              this.deps.endpointApi.addCustomParam(sourceType, {
                value: [],
                dataType: sourceType,
                dataFlow: 'internal',
                isOutput: true,
                label: sourceType,
              });
            }
          }
          this.outputEndpointIds = this.directOutputId ? [this.directOutputId] : [];
        }
        this.directSourceOutput = this.sourceEndpoint?.getValue()?.value;
        this.rebuildDirect();
        return;
      }

      // Definition mode
      const defId = this.sourceDefId;

      // Verify the definition exists
      const api = this.repeaterApi.getDefinitionApi(defId);
      if (!api) {
        logger.error('RepeaterNode', `Definition '${defId}' not found`);
        this.sourceDefId = null;
        return;
      }

      // Check for a domain adapter based on definition output types
      const outputTypes = this.repeaterApi.getDefinitionOutputTypes(defId);
      this.adapter = this.repeaterApi.getAdapter(outputTypes) ?? null;

      if (this.adapter) {
        logger.info('RepeaterNode', `Template mode: adapter matched for output types [${outputTypes.join(', ')}]`);
        // Inject adapter transform endpoints before sync so they're preserved
        this.injectTransformEndpoints();
      }

      // Sync mirror endpoints (add new, remove stale, preserve adapter endpoints)
      const preserveIds = this.adapter
        ? new Set([
            ...Object.keys(this.adapter.getTransformEndpoints()),
            ...(this.adapter.getOptionEndpoints ? Object.keys(this.adapter.getOptionEndpoints()) : []),
          ])
        : undefined;
      this.repeaterApi.syncMirrorEndpoints(defId, preserveIds);

      if (this.adapter) {
        // Classify mirror endpoints and rebuild templates
        this.classifyEndpoints();
        this.rebuildTemplated();
      }

      const t1 = performance.now();
      logger.info('RepeaterNode', `rebuild: ${(t1-t0).toFixed(1)}ms`);
    } finally {
      this.rebuilding = false;
    }
  }

  // ─── Direct mode ───────────────────────────────────────────

  private rebuildDirect(): void {
    if (!this.adapter || !this.directSourceOutput) return;

    const templateOutputs = [{
      output: this.directSourceOutput,
      slotIndices: Array.from({ length: this.params.count.value }, (_, i) => i),
    }];

    const transforms = this.gatherTransforms();
    const options = this.gatherOptions();
    this.prebuiltObjects = this.adapter.buildInstances(
      templateOutputs, transforms,
      { graphIndex: this.deps.graphIndex, instancePath: this.deps.instancePath },
      options,
    );
    this.cachedTemplateOutputs = templateOutputs;
    this.setOutput(this.prebuiltObjects);
  }

  // ─── Template mode ──────────────────────────────────────────

  /**
   * Add adapter transform endpoints (posX, posZ, rotY, etc.) to the Repeater.
   * Skips any that already exist (e.g. if the definition also exposes them).
   */
  private injectTransformEndpoints(): void {
    if (!this.adapter) return;
    const existing = new Set(this.deps.endpointApi.getEndpointIds());

    for (const [epId, config] of Object.entries(this.adapter.getTransformEndpoints())) {
      if (existing.has(epId)) continue;
      this.deps.endpointApi.addCustomParam(epId, {
        value: config.value,
        dataType: config.dataType,
        label: config.label,
        group: 'Transforms',
      });
    }

    if (this.adapter.getOptionEndpoints) {
      for (const [epId, config] of Object.entries(this.adapter.getOptionEndpoints())) {
        if (existing.has(epId)) continue;
        const epConfig: Record<string, any> = {
          value: config.value,
          dataType: config.dataType,
          label: config.label,
          group: 'Transforms',
        };
        if (config.options) epConfig.options = config.options;
        this.deps.endpointApi.addCustomParam(epId, epConfig);
      }
    }
  }

  /**
   * Classify custom param endpoints into per-instance (options), uniform, transform, and output.
   * Per-instance: has options array → creates template groups, distributes from options.
   * Uniform: no options → same value broadcast to all templates, fast update path.
   */
  private classifyEndpoints(): void {
    this.transformEndpointIds = [];
    this.optionEndpointIds = [];
    this.perInstanceEndpointIds = [];
    this.uniformEndpointIds = [];
    this.outputEndpointIds = [];

    if (!this.adapter) {
      throw new Error('classifyEndpoints called without an adapter');
    }

    // Transform + option endpoints: always from the adapter, regardless of mode
    this.transformEndpointIds = Object.keys(this.adapter.getTransformEndpoints());
    this.optionEndpointIds = this.adapter.getOptionEndpoints
      ? Object.keys(this.adapter.getOptionEndpoints())
      : [];

    const adapterNames = new Set([...this.transformEndpointIds, ...this.optionEndpointIds]);

    // Definition mode: also classify mirror endpoints from the definition API
    if (this.sourceDefId) {
      const api = this.repeaterApi.getDefinitionApi(this.sourceDefId);
      if (!api) {
        throw new Error(`classifyEndpoints: definition '${this.sourceDefId}' not found`);
      }

      for (const [name, ep] of Object.entries(api)) {
        if (ep.isOutput) {
          this.outputEndpointIds.push(name);
          continue;
        }
        if (adapterNames.has(name)) continue;
        const opts = ep.options;
        if (Array.isArray(opts) && opts.length > 0) {
          this.perInstanceEndpointIds.push(name);
        } else {
          this.uniformEndpointIds.push(name);
        }
      }
    }

    logger.trace('RepeaterNode', `classifyEndpoints: perInstance=[${this.perInstanceEndpointIds}] uniform=[${this.uniformEndpointIds}] transform=[${this.transformEndpointIds}] output=[${this.outputEndpointIds}]`);
  }

  /**
   * Fast path: only transforms changed — update InstancedMesh matrices in place.
   * No new objects created, so the scene keeps its references and renders immediately.
   */
  private updateTransformsOnly(): void {
    if (!this.adapter || this.cachedTemplateOutputs.length === 0 || this.prebuiltObjects.length === 0) return;
    const t0 = performance.now();

    const transforms = this.gatherTransforms();
    const options = this.gatherOptions();

    this.adapter.updateInstanceTransforms(
      this.prebuiltObjects,
      this.cachedTemplateOutputs,
      transforms,
      options,
    );

    // Re-propagate output (needed for non-GPU adapters like polylines
    // where the array contents are replaced rather than mutated in place)
    this.setOutput(this.prebuiltObjects);

   // const t1 = performance.now();
   // logger.info('RepeaterNode', `updateTransformsOnly: ${(t1-t0).toFixed(1)}ms (${this.prebuiltObjects.length} objects, in-place)`);
  }

  /**
   * Template mode rebuild:
   * 1. Group slots by config values → K unique groups
   * 2. Create/reuse one template graph instance per group
   * 3. Evaluate templates
   * 4. Pass to adapter.buildInstances()
   */
  private rebuildTemplated(): void {
    if (!this.adapter || !this.sourceDefId) return;
    const t0 = performance.now();

    const count = this.params.count.value;
    const customParams = this.deps.endpointApi.getCustomParams();

    // Dynamically reclassify: uniform params with non-empty array values
    // become effectively per-instance (they affect template grouping)
    const effectivePerInstance = [...this.perInstanceEndpointIds];
    const effectiveUniform: string[] = [];
    for (const epId of this.uniformEndpointIds) {
      const value = customParams[epId];
      if (Array.isArray(value) && value.length > 0) {
        effectivePerInstance.push(epId);
      } else {
        effectiveUniform.push(epId);
      }
    }

    // Step 1: Group slots by config values
    const groups = this.groupSlotsByConfig(count, customParams, effectivePerInstance);

    // Check if config actually changed — if not, just update transforms
    const configKey = `${count}|` + groups.map(g => g.configKey + ':' + g.slotIndices.length).join(',');
    if (configKey === this.cachedConfigKey && this.cachedTemplateOutputs.length > 0) {
      this.updateTransformsOnly();
      return;
    }

    const t1 = performance.now();

    // Step 2: Dispose old children and create template instances
    this.disposeChildren();

    this.repeaterApi.childIds = this.childIds;
    this.repeaterApi.beginBatch();

    const templateOutputs: { output: any; slotIndices: number[] }[] = [];

    for (const group of groups) {
      const templateId = `${this.deps.instancePath}/${this.id}~tpl${templateOutputs.length}`;
      try {
        this.repeaterApi.createChildInstance(this.sourceDefId, templateId);
        this.childIds.push(templateId);
      } catch (e) {
        logger.error('RepeaterNode', `Failed to create template instance: ${e}`);
        continue;
      }

      // Per-instance params (including promoted uniforms): pick from array value
      const repIdx = group.slotIndices[0];
      const perInstValues: string[] = [];
      for (const epId of effectivePerInstance) {
        const value = customParams[epId];
        const itemValue = pickCyclic(value, repIdx);
        if (itemValue !== undefined) {
          this.repeaterApi.setChildValue(templateId, epId, itemValue);
          perInstValues.push(`${epId}=${JSON.stringify(itemValue)}`);
        }
      }

      // Uniform params (scalar or empty): broadcast or read live value from source
      for (const epId of effectiveUniform) {
        const value = customParams[epId];
        if (value !== undefined && !(Array.isArray(value) && value.length === 0)) {
          this.repeaterApi.setChildValue(templateId, epId, value);
        } else if (this.sourceNodeId) {
          const liveValue = this.repeaterApi.getSiblingValue(this.sourceNodeId, epId);
          if (liveValue !== undefined) {
            this.repeaterApi.setChildValue(templateId, epId, liveValue);
          }
        }
      }

      // Collect output from this template instance (MeshEntry[] via scene pipeline)
      if (perInstValues.length > 0) {
        logger.trace('RepeaterNode', `template ${templateOutputs.length}: slot=${repIdx} ${perInstValues.join(' ')}`);
      }
      const output = this.collectTemplateOutput(templateId);
      templateOutputs.push({ output, slotIndices: group.slotIndices });
    }

    this.repeaterApi.childIds = this.childIds;
    this.repeaterApi.endBatch();

    // Cache for transform-only fast path
    this.cachedTemplateOutputs = templateOutputs;
    this.cachedConfigKey = configKey;

    const t2 = performance.now();

    // Step 3: Gather transforms and options from custom params
    const transforms = this.gatherTransforms();
    const options = this.gatherOptions();

    // Step 4: Build instanced output via adapter
    this.prebuiltObjects = this.adapter.buildInstances(
      templateOutputs,
      transforms,
      { graphIndex: this.deps.graphIndex, instancePath: this.deps.instancePath },
      options,
    );

    // Set output for downstream consumption
    this.setOutput(this.prebuiltObjects);

    const t3 = performance.now();
    logger.info('RepeaterNode', `rebuildTemplated: group=${(t1-t0).toFixed(1)}ms templates=${(t2-t1).toFixed(1)}ms adapter=${(t3-t2).toFixed(1)}ms total=${(t3-t0).toFixed(1)}ms (${count} slots → ${groups.length} templates → ${this.prebuiltObjects.length} objects)`);
  }

  /**
   * Group N slots into K groups by per-instance param values.
   * Uses the repeater's mirror endpoint values (which default to definition options
   * but can be overridden by the user).
   */
  private groupSlotsByConfig(count: number, customParams: Record<string, any>, perInstanceIds?: string[]): { configKey: string; slotIndices: number[] }[] {
    const epIds = perInstanceIds ?? this.perInstanceEndpointIds;
    if (epIds.length === 0) {
      // No per-instance params — all slots share one template
      return [{ configKey: '_all', slotIndices: Array.from({ length: count }, (_, i) => i) }];
    }

    const groupMap = new Map<string, number[]>();

    for (let i = 0; i < count; i++) {
      const keyParts: string[] = [];
      for (const epId of epIds) {
        const value = customParams[epId];
        const itemValue = pickCyclic(value, i);
        keyParts.push(String(itemValue ?? ''));
      }
      const key = keyParts.join('|');

      let group = groupMap.get(key);
      if (!group) {
        group = [];
        groupMap.set(key, group);
      }
      group.push(i);
    }

    return Array.from(groupMap.entries()).map(([configKey, slotIndices]) => ({ configKey, slotIndices }));
  }

  /**
   * Collect domain-specific output from a template child instance via the adapter.
   */
  private collectTemplateOutput(templateId: string): any {
    if (!this.adapter || !this.sourceDefId) return [];
    const api = this.repeaterApi.getDefinitionApi(this.sourceDefId);
    if (!api) return [];

    const outputPaths: { endpointName: string; internalNodeId: string }[] = [];
    for (const [name, ep] of Object.entries(api)) {
      if (!ep.isOutput) continue;
      const [intNodeId] = ep.path.split('.');
      outputPaths.push({ endpointName: name, internalNodeId: intNodeId });
    }

    return this.adapter.collectTemplateOutput(this.deps.graphIndex, templateId, outputPaths);
  }

  /** Push output through the typed mirror endpoint(s) or direct output */
  private setOutput(value: any[]): void {
    if (this.outputEndpointIds.length > 0) {
      for (const epId of this.outputEndpointIds) {
        this.deps.endpointApi.sendOutput(epId, value);
      }
    } else {
      // Direct mode: send through the source internal endpoint's output
      this.deps.endpointApi.sendOutput('source', value);
    }
  }

  /** Gather per-instance transform arrays from custom params */
  private gatherTransforms(): Record<string, any[]> {
    const customParams = this.deps.endpointApi.getCustomParams();
    const transforms: Record<string, any[]> = {};
    for (const epId of this.transformEndpointIds) {
      transforms[epId] = customParams[epId] ?? [];
    }
    return transforms;
  }

  /** Gather scalar adapter options from custom params */
  private gatherOptions(): Record<string, any> {
    const customParams = this.deps.endpointApi.getCustomParams();
    const options: Record<string, any> = {};
    for (const epId of this.optionEndpointIds) {
      options[epId] = customParams[epId];
    }
    return options;
  }

  /**
   * Fast path for uniform param changes: push new value to all existing templates,
   * re-collect output, and rebuild adapter output without tearing down templates.
   */
  private updateUniformParam(endpointId: string): void {
    const value = this.deps.endpointApi.getValue(endpointId);
    // Skip empty arrays — let children keep the definition default
    if (Array.isArray(value) && value.length === 0) return;
    for (const childId of this.childIds) {
      this.repeaterApi.setChildValue(childId, endpointId, value);
    }
    this.recollectAndRebuild();
  }

  /** Re-collect output from existing templates and rebuild adapter output */
  private recollectAndRebuild(): void {
    for (let i = 0; i < this.cachedTemplateOutputs.length; i++) {
      const templateId = this.childIds[i];
      this.cachedTemplateOutputs[i].output = this.collectTemplateOutput(templateId);
    }
    const transforms = this.gatherTransforms();
    const options = this.gatherOptions();
    this.prebuiltObjects = this.adapter!.buildInstances(
      this.cachedTemplateOutputs,
      transforms,
      { graphIndex: this.deps.graphIndex, instancePath: this.deps.instancePath },
      options,
    );
    this.setOutput(this.prebuiltObjects);
  }

  // ─── Lifecycle ──────────────────────────────────────────────

  animate(): void {
    // Re-evaluate transform expressions per frame
    if (this.adapter && this.deps.endpointApi.hasFunctionParams()) {
      this.updateTransformsOnly();
    }

    for (const id of this.childIds) {
      this.repeaterApi.animateChild(id);
    }
  }

  private disposeChildren(): void {
    for (const id of this.childIds) {
      this.repeaterApi.disposeChildInstance(id);
    }
    this.childIds = [];
  }

  dispose(): void {
    this.disposeChildren();
    this.prebuiltObjects = [];
    this.cachedTemplateOutputs = [];
    this.cachedConfigKey = '';
    this.sourceEndpoint = null;
    this.sourceDefId = null;
    this.sourceNodeId = null;
    this.directSourceOutput = null;
    this.directOutputId = null;
    super.dispose();
  }
}

// ─────────────────────────────────────────────────────────────
// NodeDef
// ─────────────────────────────────────────────────────────────

export const repeaterNodeDef: NodeDef<RepeaterNode> = {
  type: 'Repeater',
  shortName: 'rep',
  info: 'Creates multiple instances from a connected source.\nConnect a definition instance or bare node to the source input.\nPass arrays to distribute values across instances.',
  endpoints: repeaterEndpoints,
  allowAddParam: true,
  ctor: RepeaterNode,
};
